import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../services/api';
import styles from './BlogPostPage.module.css';

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    const loadPost = async () => {
      // A 404 is the ONLY answer that means this post genuinely does not exist.
      // Every other refusal is a failure on our side, and stating it as fact
      // ("doesn't exist") tells the visitor something untrue. `api.js` puts the
      // HTTP status on the thrown error (`error.status`), so the two are
      // distinguishable here -- same split card 28286 shipped in ProductsPage.
      setLoading(true);
      setLoadError('');
      setNotFound(false);
      setPost(null);
      try {
        const res = await api.get(`/blogs/${slug}`);
        setPost(res.post);
      } catch (error) {
        if (error?.status === 404) {
          setNotFound(true);
        } else {
          console.error('Failed to load blog post:', error);
          setLoadError('We could not load this post. Please check your connection and try again.');
        }
      } finally {
        setLoading(false);
      }
    };
    loadPost();
  }, [slug, reloadNonce]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingState}>Loading...</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={styles.page}>
        <div className={styles.notFound} role="alert">
          <h2>We couldn&apos;t load this post</h2>
          <p>{loadError}</p>
          <button
            type="button"
            className={styles.backLink}
            style={{ background: 'none', border: 0, padding: 0, font: 'inherit', cursor: 'pointer' }}
            onClick={() => setReloadNonce((n) => n + 1)}
          >
            Try again
          </button>
          <Link to="/blog" className={styles.backLink}>Back to Blog</Link>
        </div>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className={styles.page}>
        <div className={styles.notFound}>
          <h2>Post not found</h2>
          <p>The blog post you're looking for doesn't exist.</p>
          <Link to="/blog" className={styles.backLink}>Back to Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Back link */}
      <div className={styles.backNav}>
        <Link to="/blog" className={styles.backLink}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          All Posts
        </Link>
      </div>

      {/* Cover image */}
      {post.coverImageUrl && (
        <div className={styles.coverImage}>
          <img loading="lazy" src={post.coverImageUrl} alt={post.title} />
        </div>
      )}

      {/* Article */}
      <article className={styles.article}>
        <header className={styles.articleHeader}>
          <span className={styles.date}>{formatDate(post.publishedAt)}</span>
          <h1 className={styles.articleTitle}>{post.title}</h1>
          {post.excerpt && (
            <p className={styles.excerpt}>{post.excerpt}</p>
          )}
        </header>

        <div
          className={styles.articleBody}
          dangerouslySetInnerHTML={{ __html: post.body }}
        />
      </article>
    </div>
  );
};

export default BlogPostPage;
